const CONTENT_ROW_HEIGHT_ADJUSTMENT_PX = 15;

const getLogicalRowIndexFromTr = (row: HTMLTableRowElement): number | undefined => {
  const cell = row.querySelector<HTMLElement>('td[data-row-index]');
  if (!cell?.dataset.rowIndex) {
    return undefined;
  }

  const index = Number(cell.dataset.rowIndex);
  return Number.isNaN(index) ? undefined : index;
};

const getTitleRowSpan = (row: HTMLTableRowElement): number => {
  const cell = row.querySelector<HTMLTableCellElement>('td[data-row-index]');
  return cell?.rowSpan ?? 1;
};

const buildRowIndexMap = (rows: HTMLTableRowElement[]): Map<number, HTMLTableRowElement> => {
  const map = new Map<number, HTMLTableRowElement>();
  rows.forEach(row => {
    const index = getLogicalRowIndexFromTr(row);
    if (index !== undefined) {
      map.set(index, row);
    }
  });
  return map;
};

const setCellHeight = (cell: HTMLElement, height: number): void => {
  cell.style.setProperty('box-sizing', 'border-box');
  cell.style.setProperty('height', `${height}px`, 'important');
  cell.style.setProperty('vertical-align', 'middle');
};

const setRowCellsHeight = (row: HTMLTableRowElement, height: number): void => {
  Array.from(row.cells).forEach(cell => {
    setCellHeight(cell as HTMLElement, height);
  });
};

const distributeContentRowHeights = (contentRows: HTMLTableRowElement[], targetTotal: number): void => {
  if (contentRows.length === 0) {
    return;
  }

  if (contentRows.length === 1) {
    setRowCellsHeight(contentRows[0], targetTotal);
    return;
  }

  const leadingRows = contentRows.slice(0, -1);
  const lastRow = contentRows.at(-1);
  if (!lastRow) {
    return;
  }
  const leadingHeight = leadingRows.reduce((sum, row) => sum + row.offsetHeight, 0);
  setRowCellsHeight(lastRow, Math.max(targetTotal - leadingHeight, 0));
};

const getContentRowsForTitleIndex = (
  logicalRowIndex: number,
  rowSpan: number,
  contentRowMap: Map<number, HTMLTableRowElement>
): HTMLTableRowElement[] => {
  const contentRows: HTMLTableRowElement[] = [];
  for (let offset = 0; offset < rowSpan; offset += 1) {
    const contentRow = contentRowMap.get(logicalRowIndex + offset);
    if (contentRow) {
      contentRows.push(contentRow);
    }
  }
  return contentRows;
};

const equalizeTitleContentRowBlock = (
  titleRow: HTMLTableRowElement,
  contentRows: HTMLTableRowElement[],
  contentHeightAdjustment = 0
): void => {
  if (contentRows.length === 0) {
    return;
  }

  const titleHeight = titleRow.offsetHeight;
  const contentHeight = contentRows.reduce((sum, row) => sum + row.offsetHeight, 0);
  if (Math.abs(titleHeight - contentHeight) <= 1) {
    return;
  }

  const target = Math.max(titleHeight, contentHeight);
  const titleCell = titleRow.cells[0] as HTMLElement | undefined;
  if (titleCell) {
    setCellHeight(titleCell, target);
  }

  if (contentHeight < target) {
    distributeContentRowHeights(contentRows, target);

    if (contentHeightAdjustment > 0) {
      const residual = contentRows.reduce((sum, row) => sum + row.offsetHeight, 0) - titleRow.offsetHeight;
      if (residual > 0) {
        const lastRow = contentRows[contentRows.length - 1];
        const lastCell = lastRow.cells[0] as HTMLElement;
        const parsedHeight = Number.parseFloat(lastCell.style.height);
        if (!Number.isNaN(parsedHeight)) {
          setCellHeight(lastCell, parsedHeight - residual);
        }
      }
    }
  }
};

/**
 * When the title column wraps long text, its rows can grow taller than the
 * corresponding rows in the content column (separate <table> elements in a flex
 * container). Read heights from the live DOM and write them onto the content
 * cells so both columns align (export preview, html2canvas capture, etc.).
 */
export const syncSpreadsheetTitleContentRowHeights = (
  titlesContainer: HTMLElement,
  contentContainer: HTMLElement
): void => {
  const titleRowMap = buildRowIndexMap(
    Array.from(titlesContainer.querySelectorAll<HTMLTableRowElement>('table tbody tr'))
  );
  const contentRowMap = buildRowIndexMap(
    Array.from(contentContainer.querySelectorAll<HTMLTableRowElement>('table tbody tr'))
  );

  titleRowMap.forEach((titleRow, logicalRowIndex) => {
    const rowSpan = getTitleRowSpan(titleRow);
    const contentRows = getContentRowsForTitleIndex(logicalRowIndex, rowSpan, contentRowMap);
    if (contentRows.length === 0) {
      return;
    }

    const titleHeight = titleRow.offsetHeight;
    const contentHeight = contentRows.reduce((sum, row) => sum + row.offsetHeight, 0);
    if (titleHeight <= contentHeight) {
      return;
    }

    equalizeTitleContentRowBlock(titleRow, contentRows, CONTENT_ROW_HEIGHT_ADJUSTMENT_PX);
  });
};

/**
 * Bi-directional row-height equalization between the titles column and the
 * content columns. Pairs rows by logical config index (`data-row-index`) so
 * title/content tables stay aligned even when rowspan collapses title-side rows.
 * Unlike {@link syncSpreadsheetTitleContentRowHeights}, this also expands title-side
 * cells when the content side is taller, so it is safe to run continuously in the
 * regular UI (not just during PDF capture).
 */
export const equalizeSpreadsheetTitleContentRowHeights = (
  titlesContainer: HTMLElement,
  contentContainer: HTMLElement
): void => {
  const titleRowMap = buildRowIndexMap(
    Array.from(titlesContainer.querySelectorAll<HTMLTableRowElement>('table tbody tr'))
  );
  const contentRowMap = buildRowIndexMap(
    Array.from(contentContainer.querySelectorAll<HTMLTableRowElement>('table tbody tr'))
  );

  titleRowMap.forEach((titleRow, logicalRowIndex) => {
    const rowSpan = getTitleRowSpan(titleRow);
    const contentRows = getContentRowsForTitleIndex(logicalRowIndex, rowSpan, contentRowMap);
    equalizeTitleContentRowBlock(titleRow, contentRows);
  });
};

/** Runs {@link syncSpreadsheetTitleContentRowHeights} for every titles/content pair under `root`. */
export const syncSpreadsheetTitleContentRowHeightsInSubtree = (root: HTMLElement): void => {
  root.querySelectorAll<HTMLElement>('.titles-columns-container').forEach(titlesContainer => {
    const parent = titlesContainer.parentElement;
    if (!parent) {
      return;
    }

    const contentContainer = parent.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
    if (!contentContainer) {
      return;
    }

    syncSpreadsheetTitleContentRowHeights(titlesContainer, contentContainer);
  });
};
